import { Elements } from '@stripe/react-stripe-js';
import { NewCardPaymentElementData, useNewCardPaymentElement } from './useNewCardPaymentElement';
import { SetupIntentContext, useCreateSetupIntent } from './useSetupIntent';
import { Stripe, StripeElementsOptions, loadStripe } from '@stripe/stripe-js';
import { useMountEffect } from '@tock/hooks';
import { useState } from 'react';

type ElementRenderProp = (data: NewCardPaymentElementData) => React.ReactNode;
interface Props {
  stripePublicKey: string;
  children?: React.ReactNode | ElementRenderProp | undefined;
}

let stripePromise: Promise<Stripe | null> | null;
function initializeStripe(stripePublicKey: string) {
  if (stripePromise !== null) {
    stripePromise = loadStripe(stripePublicKey);
  }
}

export const ElementProvider: React.FC<Props> = (props) => {
  // We only set this state variable in our mount effect to force another re-render after Stripe has initialized.
  // Otherwise, we get a loading issue where we initialize Stripe but not before the first render, where we return null below because stripePromise is null.
  const [, setIsStripeInitialized] = useState(false);
  const { children, stripePublicKey } = props;

  useMountEffect(() => {
    initializeStripe(stripePublicKey);
    setIsStripeInitialized(true);
  });

  const setupIntent = useCreateSetupIntent();
  if (!stripePromise) {
    return null;
  }

  const options: StripeElementsOptions = {
    mode: 'setup',
    paymentMethodTypes: ['card'],
    fonts: [
      {
        display: 'fallback',
        family: 'Oracle',
        weight: '400',
        src: `url('https://www.exploretock.com/fonts/oracle/ABCOracle-Regular.woff2') format('woff2'),
        url('https://www.exploretock.com/fonts/oracle/ABCOracle-Regular.woff') format('woff'),
        url('https://www.exploretock.com/fonts/oracle/ABCOracle-Regular.otf') format('truetype')`,
      },
      {
        display: 'fallback',
        family: 'Oracle',
        weight: '500',
        src: `url('https://www.exploretock.com/fonts/oracle/ABCOracle-Medium.woff2') format('woff2'),
        url('https://www.exploretock.com/fonts/oracle/ABCOracle-Medium.woff') format('woff'),
        url('https://www.exploretock.com/fonts/oracle/ABCOracle-Medium.otf') format('truetype')`,
      },
      {
        display: 'fallback',
        family: 'Oracle',
        weight: '700',
        src: `url('https://www.exploretock.com/fonts/oracle/ABCOracle-Bold.woff2') format('woff2'),
        url('https://www.exploretock.com/fonts/oracle/ABCOracle-Bold.woff') format('woff'),
        url('https://www.exploretock.com/fonts/oracle/ABCOracle-Bold.otf') format('truetype')`,
      },
    ],
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <SetupIntentContext.Provider value={setupIntent}>
        <ElementProviderInternal children={children} />
      </SetupIntentContext.Provider>
    </Elements>
  );
};

const ElementProviderInternal = ({ children }: Pick<Props, 'children'>) => {
  const newCardPaymentElementData = useNewCardPaymentElement();
  if (typeof children === 'function') {
    return children(newCardPaymentElementData);
  }
  return children;
};
